import { evaluateWarrantyDate, WARRANTY_MODIFIER } from '../utils/evaluateWarrantyDates';
import { formatDate, checkDateValidity } from '../../../../Foundation/utils';
import { showToast, ToastType } from '../../toast';
import { renderDropdownItems, updateActionVisibility } from './action-handler';
import {
  isExpiredForFlow,
  isEarmouldDisabledForFlow,
  isLossAndDamageUsed,
} from '../utils/flow-expiry';
import {
  initMobileSortDropdown,
  findHeaderByColumnKey,
  getNextSortState,
  updateHeaderSortState,
  updateMobileSortSelection,
} from './sort-handler';
import { writeSessionStorageJson, SERVICE_FLOW_STORAGE_KEYS } from '../storage';

const PAGE_SIZE = 20;

export class ServiceResultsTable {
  /**
   * Creates a new instance of a ServiceResultsTable component.
   * @param {HTMLElement} element ServiceResultsTable DOM element
   */
  constructor(element) {
    this.el = element;

    const loadMoreId = element.dataset.loadMoreId;
    const loadMoreContainer = loadMoreId
      ? document.querySelector(`[data-service-results-table-for="${loadMoreId}"]`)
      : null;

    const nextStepBar = document.querySelector('[data-next-step-bar]');

    this._refs = {
      thead: element.querySelector('.service-results-table__head tr'),
      selectAllCheckbox: null,
      mobileSelectAllCheckbox: null,
      sortableHeaders: [],
      searchInput: document.querySelector('[data-serial-text]'),
      searchButton: document.querySelector('[data-serial-search-button]'),
      searchErrorMessage: document.querySelector('[data-serial-text-error]'),
      mobileSortBtn: element.querySelector('.service-results-table__mobile-sort-btn'),
      mobileToolbar: element.querySelector('.service-results-table__mobile-toolbar'),
      mobileSortWrapper: null,
      mobileSortMenu: null,
      tbody: element.querySelector('.service-results-table__body'),
      scrollWrapper: element.querySelector('.service-results-table__scroll-wrapper'),
      loadMoreContainer,
      loadMoreBtn: loadMoreContainer ? loadMoreContainer.querySelector('button') : null,
      loadMoreCounter: loadMoreContainer
        ? loadMoreContainer.querySelector('[data-service-results-table-counter]')
        : null,
      nextStepBar,
      nextStepBarCount: nextStepBar ? nextStepBar.querySelector('.next-step-bar__count') : null,
      continueBtn: nextStepBar
        ? nextStepBar.querySelector('.next-step-bar__button--primary')
        : null,
      viewSelectedBtn: nextStepBar
        ? nextStepBar.querySelector('.next-step-bar__button--secondary')
        : null,
    };

    this._abortController = null;
    this._isLoading = false;
    this._updatingToast = null;

    window.addEventListener('pageshow', () => {
      this._setContinueLoading(false);
      this._actionNavigationInProgress = false;
    });

    this._state = {
      activeColumn: null,
      direction: null,
      skip: 0,
      currentSearch: null,
      totalVisible: 0,
      totalCount: null,
      rowsData: [],
      // Keeps the raw item behind each selected serial number
      selectedItemsData: new Map(),
    };

    // Tracks serial numbers already checked for a product image via fetchImages()
    this._imageCheckedSerials = new Set();

    this._config = this._parseConfig();

    if (!this._config) return;

    this._columns = this._config.Columns || [];

    // Derive flags from column types for convenience
    this._showCheckboxes = this._columns.some((col) => col.type === 'checkbox');
    this._actionsCol = this._columns.find((col) => col.type === 'actions') || null;
    this._actionsDropdown = this._actionsCol ? this._actionsCol.dropdown === true : false;

    this._warrantyKey = !this._actionsDropdown
      ? this._columns.find((col) => col.type === 'warranty')?.key || null
      : null;

    if (this._showCheckboxes) {
      this.el.setAttribute('data-show-checkboxes', '');
    }

    this._renderHeader();

    // Re-query sortable headers after dynamic render
    this._refs.sortableHeaders = Array.from(
      element.querySelectorAll('.service-results-table__th--sortable'),
    );
    this._initMobileSortDropdown();
    this.el.classList.add('is-hidden');
    this._createResultsAnnouncer();
    this._addEventListeners();
    this._initSearchEvents();
  }

  /**
   * Returns the selected serial numbers.
   * Selection is tracked in component state so it survives re-renders
   * (e.g. sorting, load more) where the DOM checkboxes are recreated.
   * @returns {string[]}
   */
  getSelected() {
    return [...this._state.selectedItemsData.keys()];
  }

  /**
   * Returns the number of currently selected items.
   * @returns {number}
   */
  getSelectedCount() {
    return this._state.selectedItemsData.size;
  }

  /**
   * Re-applies the persisted selection state
   * checkboxes. Called after rows are (re)rendered so selections survive
   * sorting and load-more.
   */
  _applySelectionState() {
    if (!this._showCheckboxes) return;
    this._refs.tbody.querySelectorAll('.service-results-table__checkbox').forEach((cb) => {
      cb.checked = this._state.selectedItemsData.has(cb.value);
    });
    this._updateSelectAllState();
  }

  /**
   * Removes all event listeners and cleans up the component.
   */
  destroy() {
    this._abortController?.abort();
    this._abortController = null;
    this._toggleLoading(false);
    this._setUpdatingMessageVisible(false);
    this._refs.sortableHeaders.forEach((th) => {
      th.replaceWith(th.cloneNode(true));
    });

    if (this._refs.searchButton) {
      this._refs.searchButton.replaceWith(this._refs.searchButton.cloneNode(true));
    }

    if (this._refs.searchInput) {
      this._refs.searchInput.replaceWith(this._refs.searchInput.cloneNode(true));
    }

    if (this._refs.loadMoreBtn) {
      this._refs.loadMoreBtn.replaceWith(this._refs.loadMoreBtn.cloneNode(true));
    }

    if (this._refs.mobileSortMenu) {
      this._refs.mobileSortMenu.remove();
    }

    if (this._refs.mobileSortWrapper) {
      this._refs.mobileSortWrapper.replaceWith(this._refs.mobileSortWrapper.cloneNode(true));
    }

    if (this._refs.nextStepBar) {
      this._refs.nextStepBar.setAttribute('aria-hidden', 'true');
      if (this._refs.nextStepBarCount) {
        this._refs.nextStepBarCount.textContent = '0';
      }
    }
  }

  /**
   * Renders <th> elements into <thead><tr> based on this._config.columns.
   * Column types:
   *   - "checkbox"  -> empty checkbox header cell
   *   - "actions"   -> actions header cell (always last by convention)
   *   - anything else -> standard text header, optionally sortable
   */
  _renderHeader() {
    if (!this._refs.thead) return;

    this._refs.thead.innerHTML = '';

    for (const col of this._columns) {
      const th = document.createElement('th');

      if (col.type === 'checkbox') {
        th.className = 'service-results-table__th service-results-table__th--checkbox';
        th.setAttribute('scope', 'col');

        const label = document.createElement('label');
        label.className = 'service-results-table__select-all';
        label.hidden = true;

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'service-results-table__checkbox';
        checkbox.setAttribute('aria-label', this._config.Translations?.selectAllCheckboxLabel);

        const text = document.createElement('span');
        text.textContent = this._config.Translations?.selectAllCheckboxLabel;

        label.append(checkbox, text);
        th.appendChild(label);
        this._refs.selectAllCheckbox = checkbox;

        if (this._refs.mobileToolbar) {
          const mobileLabel = label.cloneNode(true);
          mobileLabel.classList.add('service-results-table__select-all--mobile');
          mobileLabel.hidden = true;
          this._refs.mobileToolbar.insertBefore(mobileLabel, this._refs.mobileSortBtn);
          this._refs.mobileSelectAllCheckbox = mobileLabel.querySelector('input[type="checkbox"]');
        }
      } else if (col.type === 'actions') {
        th.className = 'service-results-table__th service-results-table__th--actions';
        th.textContent = col.label || '';
      } else if (col.sortable) {
        th.className = 'service-results-table__th service-results-table__th--sortable';
        th.dataset.sort = col.key;
        th.setAttribute('aria-sort', 'none');
        th.setAttribute('tabindex', '0');

        const inner = document.createElement('span');
        inner.className = 'service-results-table__th-inner';
        inner.textContent = col.label || '';

        const icon = document.createElement('span');
        icon.className = 'service-results-table__sort-icon';
        icon.setAttribute('aria-hidden', 'true');

        inner.appendChild(icon);
        th.appendChild(inner);
      } else {
        th.className = 'service-results-table__th';
        th.textContent = col.label || '';
      }

      this._refs.thead.appendChild(th);
    }
  }

  /**
   * Builds the mobile "Sort by" dropdown from the sortable columns.
   * See sort-handler.js for the underlying Dropable/ui-dropdown wiring.
   */
  _initMobileSortDropdown() {
    const result = initMobileSortDropdown(this._refs.mobileSortBtn, this._columns, (col) => {
      const th = findHeaderByColumnKey(this._refs.sortableHeaders, col.key);
      if (th) this._handleSortClick(th);
    });

    if (!result) return;

    this._refs.mobileSortWrapper = result.wrapper;
    this._refs.mobileSortMenu = result.menu;
  }

  _addEventListeners() {
    this._refs.sortableHeaders.forEach((th) => {
      th.addEventListener('click', () => this._handleSortClick(th));
      th.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._handleSortClick(th);
        }
      });
    });

    if (this._refs.loadMoreBtn) {
      this._refs.loadMoreBtn.addEventListener('click', () => this._handleLoadMore());
    }

    if (this._showCheckboxes) {
      [this._refs.selectAllCheckbox, this._refs.mobileSelectAllCheckbox]
        .filter(Boolean)
        .forEach((checkbox) => {
          checkbox.addEventListener('change', (event) => {
            this._toggleAllVisibleRows(event.target.checked);
          });
        });

      this._refs.tbody.addEventListener('change', (e) => {
        if (e.target.classList.contains('service-results-table__checkbox')) {
          this._syncCheckboxes(e.target);
        }
      });

      this._refs.tbody.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action-sidebar]');

        if (!btn) return;

        const row = btn.closest('tr');
        const serialNo =
          row?.querySelector(
            '.service-results-table__checkbox:not(.service-results-table__checkbox--mobile)',
          )?.value ||
          row?.dataset?.serialNo ||
          null;

        document.dispatchEvent(
          new CustomEvent('related-items:open', {
            detail: { tableInstance: this, serialNo },
          }),
        );
      });

      if (this._refs.viewSelectedBtn) {
        this._refs.viewSelectedBtn.addEventListener('click', () => {
          document.dispatchEvent(
            new CustomEvent('selected-items:open', {
              detail: { tableInstance: this },
            }),
          );
        });
      }

      if (this._refs.continueBtn) {
        this._refs.continueBtn.addEventListener('click', () => this._continueToNextStep());
      }
    }
  }

  async _continueToNextStep() {
    const nextStepUrl = this._config.NextStepUrl;
    if (!nextStepUrl || !this.getSelectedCount() || this._refs.continueBtn?.disabled) return;

    const selectedItems = new Map(this._state.selectedItemsData);
    this._setContinueLoading(true);

    try {
      await this.fetchImages(selectedItems);

      const items = [...selectedItems.entries()].map(([serialNo, item]) =>
        this._buildServiceFlowItem(item, serialNo),
      );

      if (!items.length) {
        this._setContinueLoading(false);
        return;
      }

      writeSessionStorageJson(SERVICE_FLOW_STORAGE_KEYS.ITEMS, items);
      window.location.href = nextStepUrl;
    } catch (error) {
      this._setContinueLoading(false);
      throw error;
    }
  }

  async _handleRowActionClick(event, serialNo, actionLink) {
    event.preventDefault();

    const actionUrl = actionLink.getAttribute('href');
    if (this._actionNavigationInProgress || !serialNo || !actionUrl || actionUrl.startsWith('#')) {
      return;
    }

    const item = this._state.rowsData.find(
      (rowData) => String(rowData.SerialNo || '') === serialNo,
    );
    if (!item) return;

    this._actionNavigationInProgress = true;

    try {
      await this.fetchImages(new Map([[serialNo, item]]));

      const items = [this._buildServiceFlowItem(item)];
      if (!writeSessionStorageJson(SERVICE_FLOW_STORAGE_KEYS.ITEMS, items)) {
        this._actionNavigationInProgress = false;
        return;
      }

      window.location.href = actionUrl;
    } catch (error) {
      this._actionNavigationInProgress = false;
      console.error('Service flow action failed:', error);
    }
  }

  _buildServiceFlowItem(item, selectedSerialNo = '') {
    const serialNo = String(selectedSerialNo || item.SerialNo || '');
    const row = this._refs.tbody.querySelector(`tr[data-serial-no="${CSS.escape(serialNo)}"]`);

    return {
      serialNo,
      patientName: item.Patient || '',
      productName: item.Description || '',
      imageUrl: row?.dataset?.imageUrl || item.ImageUrl || '',
      repairWarranty: item.RepairWarrantyEndDate || '',
    };
  }

  _setContinueLoading(isLoading) {
    const button = this._refs.continueBtn;
    if (!button) return;

    button.classList.toggle('next-step-bar__button--loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));
  }

  _initSearchEvents() {
    if (this._refs.searchButton && this._refs.searchInput) {
      this._refs.searchButton.addEventListener('click', (e) => {
        e.preventDefault();
        this._triggerSearch();
      });

      this._refs.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._triggerSearch();
        }
      });

      this._refs.searchInput.addEventListener('input', () => {
        this._toggleSearchErrorMessage(false);
      });
    }
  }

  _toggleSearchErrorMessage(visible) {
    if (this._refs.searchErrorMessage) {
      this._refs.searchErrorMessage.classList.toggle('is-hidden', !visible);
    }

    if (this._refs.searchInput) {
      this._refs.searchInput.classList.toggle('service-search__input--error', visible);
    }
  }

  _triggerSearch() {
    const searchWord = this._refs.searchInput.value.trim();

    if (searchWord) {
      this._toggleSearchErrorMessage(false);
      this._state.skip = 0;
      this._state.totalVisible = 0;
      this._state.currentSearch = searchWord;
      this._fetchWarrantyData(searchWord, false);
    } else {
      this._toggleSearchErrorMessage(true);
      this._refs.searchInput.focus();
    }
  }

  resetForNewSearch() {
    this._abortController?.abort();
    this._abortController = null;
    this._toggleLoading(false);
    this._setUpdatingMessageVisible(false);

    this._state.activeColumn = null;
    this._state.direction = null;
    this._state.skip = 0;
    this._state.currentSearch = null;
    this._state.totalVisible = 0;
    this._state.totalCount = null;
    this._state.rowsData = [];
    this._state.selectedItemsData.clear();
    this._imageCheckedSerials.clear();

    if (this._refs.searchInput) {
      this._refs.searchInput.value = '';
    }

    this._toggleSearchErrorMessage(false);
    this._refs.tbody.innerHTML = '';
    this.el.classList.add('is-empty', 'is-hidden');
    this._setLoadMoreVisible(false);

    if (this._refs.loadMoreCounter) {
      this._refs.loadMoreCounter.textContent = '';
    }

    this._updateSelectAllState();
    this._updateNextStepBar();
    updateHeaderSortState(this._refs.sortableHeaders, null, null);
    updateMobileSortSelection(this._refs.mobileSortMenu, null);

    window.scrollTo({ top: 0, behavior: 'smooth' });
    this._refs.searchInput?.focus({ preventScroll: true });
  }

  _handleLoadMore() {
    if (this._state.currentSearch) {
      this._fetchWarrantyData(this._state.currentSearch, true);
    }
  }

  _handleSortClick(th) {
    if (this._isLoading) return;

    const nextState = getNextSortState(th, this._refs.sortableHeaders, this._columns);
    if (!nextState) return;

    this._state.activeColumn = nextState.key;
    this._state.direction = nextState.direction;
    updateMobileSortSelection(this._refs.mobileSortMenu, this._state.activeColumn);

    if (this._state.currentSearch) {
      this._state.skip = 0;
      this._state.totalVisible = 0;
      this._state.totalCount = null;
      this._fetchWarrantyData(this._state.currentSearch, false);
    }
  }

  _syncCheckboxes(changedCheckbox) {
    const row = changedCheckbox.closest('tr');
    if (!row) return;
    row.querySelectorAll('.service-results-table__checkbox').forEach((cb) => {
      cb.checked = changedCheckbox.checked;
    });

    if (changedCheckbox.checked) {
      const item = this._state.rowsData.find(
        (i) => String(i.SerialNo || '') === changedCheckbox.value,
      );
      if (item) this.setSelectedItemData(changedCheckbox.value, item);
    } else {
      this.removeSelectedItemData(changedCheckbox.value);
    }

    this._updateSelectAllState();
    this._updateNextStepBar();
  }

  _getVisibleRowCheckboxes() {
    if (!this._showCheckboxes) return [];

    return Array.from(
      this._refs.tbody.querySelectorAll(
        '.service-results-table__checkbox:not(.service-results-table__checkbox--mobile)',
      ),
    );
  }

  _toggleAllVisibleRows(checked) {
    const visibleCheckboxes = this._getVisibleRowCheckboxes();

    visibleCheckboxes.forEach((checkbox) => {
      const row = checkbox.closest('tr');
      row?.querySelectorAll('.service-results-table__checkbox').forEach((rowCheckbox) => {
        rowCheckbox.checked = checked;
      });

      const item = this._state.rowsData.find(
        (rowData) => String(rowData.SerialNo || '') === checkbox.value,
      );

      if (checked && item) {
        this.setSelectedItemData(checkbox.value, item);
      } else if (!checked) {
        this.removeSelectedItemData(checkbox.value);
      }
    });

    this._updateSelectAllState();
    this._updateNextStepBar();
  }

  _updateSelectAllState() {
    const selectAllCheckboxes = [
      this._refs.selectAllCheckbox,
      this._refs.mobileSelectAllCheckbox,
    ].filter(Boolean);
    if (!selectAllCheckboxes.length) return;

    const visibleCheckboxes = this._getVisibleRowCheckboxes();
    const selectedCount = visibleCheckboxes.filter((checkbox) => checkbox.checked).length;
    const hasMultipleVisibleRows = visibleCheckboxes.length > 1;

    selectAllCheckboxes.forEach((selectAllCheckbox) => {
      const selectAllControl = selectAllCheckbox.closest('.service-results-table__select-all');
      selectAllControl?.toggleAttribute('hidden', !hasMultipleVisibleRows);
      selectAllCheckbox.checked =
        hasMultipleVisibleRows && selectedCount === visibleCheckboxes.length;
      selectAllCheckbox.indeterminate =
        hasMultipleVisibleRows && selectedCount > 0 && selectedCount < visibleCheckboxes.length;
      selectAllCheckbox.disabled = !hasMultipleVisibleRows;
    });
  }

  _getSelectedCheckboxes() {
    if (!this._showCheckboxes) return [];

    return Array.from(
      this._refs.tbody.querySelectorAll(
        '.service-results-table__checkbox:not(.service-results-table__checkbox--mobile):checked',
      ),
    );
  }

  /**
   * Stores/updates the raw item behind a selected serial number
   * @param {object} item
   */
  setSelectedItemData(serialNo, item) {
    this._state.selectedItemsData.set(String(serialNo), item);
  }

  /**
   * Removes the persisted item data for a serial number.
   * @param {string} serialNo
   */
  removeSelectedItemData(serialNo) {
    const key = String(serialNo);

    this._state.selectedItemsData.delete(key);
    this._imageCheckedSerials.delete(key);
  }

  /**
   * Returns the display data for all currently selected items, regardless of
   * whether their rows are still rendered in the table.
   * @returns {object[]}
   */
  getSelectedItemsData() {
    const warrantyColumn = this._getWarrantyColumn?.();

    return [...this._state.selectedItemsData.keys()].map((serialNo) => {
      const item = this._state.selectedItemsData.get(serialNo) || {};

      return {
        serialNo,
        patient: item.Patient || '',
        description: item.Description || '',
        warrantyLabel: warrantyColumn?.label || '',
        warrantyDate:
          (this._warrantyKey && item[this._warrantyKey]) || item.RepairWarrantyEndDate || '',
        imageUrl: item.ImageUrl || '',
      };
    });
  }

  /**
   * Finds the column config for the active warranty column.
   * @returns {object|undefined}
   */
  _getWarrantyColumn() {
    const columns = this._columns || [];
    return (
      columns.find((c) => c.key === this._warrantyKey) || columns.find((c) => c.type === 'warranty')
    );
  }

  _updateNextStepBar() {
    const { nextStepBar, nextStepBarCount } = this._refs;
    if (!nextStepBar) return;

    const count = this.getSelectedCount();

    if (nextStepBarCount) {
      nextStepBarCount.textContent = String(count);
    }

    if (count === 0) {
      nextStepBar.setAttribute('aria-hidden', 'true');
    } else {
      nextStepBar.removeAttribute('aria-hidden');
    }
  }

  async _fetchWarrantyData(searchWord, append = false) {
    this._abortController?.abort();
    const controller = new AbortController();
    this._abortController = controller;

    const endpoint = this._config.Endpoint;

    this._toggleLoading(true, append);
    this._updateSelectAllState();
    this.el.classList.remove('is-hidden');

    const sortCol = this._state.activeColumn || 'SerialNo';
    const sortDir = this._state.direction === 'descending' ? 'Descending' : 'Ascending';

    let loadMoreVisible = false;
    this._setUpdatingMessageVisible(false);

    const payload = {
      UserId: this._config.UserId,
      APIKey: this._config.ApiKey,
      SearchWord: searchWord,
      Skip: this._state.skip,
      Take: PAGE_SIZE,
      Brands: this._config.Brands,
      SortColumn: sortCol,
      SortDirection: sortDir,
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Server error. HTTP status: ${response.status}`);
      }

      const data = await response.json();

      if (this._abortController !== controller) return;

      this._setUpdatingMessageVisible(data.IsUpdating === true);

      const rowsData = data.Warranties || [];

      if (!append) {
        this._refs.tbody.innerHTML = '';
        this._state.totalVisible = 0;
        this._state.totalCount = null;
        this._state.rowsData = [];
        this._updateNextStepBar();
      }

      if (data.TotalCount != null) {
        this._state.totalCount = data.TotalCount;
      }

      if (rowsData.length > 0) {
        this.el.classList.remove('is-empty');
        const fragment = document.createDocumentFragment();
        rowsData.forEach((item) => fragment.appendChild(this._createRow(item)));
        this._refs.tbody.appendChild(fragment);
        this._state.rowsData = [...this._state.rowsData, ...rowsData];
        this._state.skip += PAGE_SIZE;
        this._state.totalVisible += rowsData.length;
        this._adjustBadges();
        this._applySelectionState();
        this._updateNextStepBar();

        if (this._actionsDropdown) {
          renderDropdownItems(
            this._refs.tbody,
            this._actionsCol.items || [],
            this._config.Translations?.slideInTranslations?.cancelLabel,
            (event, serialNo, actionLink) =>
              this._handleRowActionClick(event, serialNo, actionLink),
          );
          updateActionVisibility(this._refs.tbody, this._state.rowsData, this._config);
        }
      } else if (!append) {
        if (!searchWord) {
          this.el.classList.add('is-hidden');
          this._refs.tbody.innerHTML = '';
        } else {
          this.el.classList.add('is-empty');
          this._refs.tbody.innerHTML = '';
          this._refs.tbody.appendChild(this._renderEmptyState(searchWord));
        }
      }

      this._updateSelectAllState();

      const hasMore =
        this._state.totalCount != null
          ? this._state.totalVisible < this._state.totalCount
          : rowsData.length === PAGE_SIZE;
      this._updateCounter();
      this._announce(`${this._state.totalVisible} results loaded.`);

      updateHeaderSortState(
        this._refs.sortableHeaders,
        this._state.activeColumn,
        this._state.direction,
      );

      loadMoreVisible = hasMore;
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('An error occurred while fetching warranty data:', error);
      this._setUpdatingMessageVisible(false);
      if (!append) {
        this._refs.tbody.innerHTML = '';
      }
    } finally {
      if (this._abortController === controller) {
        this._toggleLoading(false, append);
        this._setLoadMoreVisible(loadMoreVisible);
        this._forceLayoutRefresh();
      }
    }
  }

  _forceLayoutRefresh() {
    if (!this.el || this.el.classList.contains('is-hidden')) return;

    void this.el.offsetHeight;
  }

  _setLoadMoreVisible(visible) {
    const { loadMoreContainer } = this._refs;
    if (loadMoreContainer) {
      if (visible) {
        loadMoreContainer.classList.remove('service-results-table__load-more--hidden');
      } else {
        loadMoreContainer.classList.add('service-results-table__load-more--hidden');
      }
    }
  }

  _setUpdatingMessageVisible(visible) {
    const isToastActive = !!this._updatingToast && !this._updatingToast.isDismissed;

    if (visible) {
      if (isToastActive) return;

      const toast = showToast({
        type: ToastType.WARNING,
        message: this._config.Translations.isUpdatingWarrantiesMessage,
        autoDismiss: 5000,
        iconKey: 'settings',
        onClose: () => {
          if (this._updatingToast === toast) this._updatingToast = null;
        },
      });
      this._updatingToast = toast;

      return;
    }

    if (isToastActive) {
      this._updatingToast.dismiss();
    }
  }

  _updateCounter() {
    const { loadMoreCounter } = this._refs;
    if (!loadMoreCounter) return;

    const { totalVisible, totalCount } = this._state;
    const ofLabel = this._config.Translations.ofLabel || 'of';

    loadMoreCounter.textContent =
      totalCount != null ? `${totalVisible} ${ofLabel} ${totalCount}` : String(totalVisible);
  }

  _createResultsAnnouncer() {
    if (this.el.querySelector('.service-results-table__announcer')) return;

    const announcer = document.createElement('div');
    announcer.className = 'service-results-table__announcer sr-only';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    this.el.appendChild(announcer);

    this._refs.announcer = announcer;
  }

  _announce(message) {
    if (!this._refs.announcer) return;
    this._refs.announcer.textContent = '';
    requestAnimationFrame(() => {
      this._refs.announcer.textContent = message;
    });
  }

  _createSkeletonCell(extraClass = '') {
    const td = document.createElement('td');
    td.className = [
      'service-results-table__cell',
      'service-results-table__cell--skeleton',
      extraClass,
    ]
      .filter(Boolean)
      .join(' ');
    const span = document.createElement('span');
    span.className = 'skeleton-loading';
    td.appendChild(span);
    return td;
  }

  _createSkeletonRows() {
    const tr = document.createElement('tr');
    tr.className = 'service-results-table__row service-results-table__row--skeleton';

    // Skeleton mirrors the column count from config (all types included)
    for (let i = 0; i < this._columns.length; i++) {
      const col = this._columns[i];
      const extraClass =
        col.type === 'checkbox' ? 'service-results-table__cell--checkbox-desktop' : '';
      tr.appendChild(this._createSkeletonCell(extraClass));
    }

    return tr;
  }

  _toggleLoading(isLoading, append = false) {
    this._isLoading = isLoading;
    if (this._refs.searchButton) {
      this._refs.searchButton.disabled = isLoading;
    }
    if (this._refs.loadMoreBtn) {
      this._refs.loadMoreBtn.disabled = isLoading;
    }
    if (!append) {
      this.el.classList.toggle('is-loading', isLoading);
    }

    if (!append) {
      if (isLoading) {
        this._refs.tbody.innerHTML = '';
        this._refs.tbody.appendChild(this._createSkeletonRows());
        this._refs.tbody.classList.add('service-results-table__body--skeleton');
        this._setLoadMoreVisible(false);
      } else {
        this._refs.tbody.classList.remove('service-results-table__body--skeleton');
      }
    } else {
      if (isLoading) {
        const skeletonRow = this._createSkeletonRows();
        skeletonRow.dataset.skeletonAppend = '';
        this._refs.tbody.appendChild(skeletonRow);
        this._setLoadMoreVisible(false);
      } else {
        const skeletonRow = this._refs.tbody.querySelector('[data-skeleton-append]');
        if (skeletonRow) {
          skeletonRow.remove();
        }
      }
    }
  }

  async fetchImages(itemsBySerial = this._state.selectedItemsData) {
    if (window.location.hostname === 'localhost') return;

    const rowsToFetch = [...itemsBySerial.keys()].filter((serialNo) => {
      const item = itemsBySerial.get(serialNo);
      return serialNo && item && !item.ImageUrl && !this._imageCheckedSerials.has(serialNo);
    });

    if (!rowsToFetch.length) return;

    try {
      const response = await fetch('/api/warranty/serialnumbers/getwarranties', {
        method: 'POST',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify(rowsToFetch),
      });

      const data = response.ok ? await response.json() : [];
      const warrantiesBySerial = new Map(
        (data || []).map((warranty) => [String(warranty.SerialNo || ''), warranty]),
      );

      for (const serialNo of rowsToFetch) {
        const warranty = warrantiesBySerial.get(serialNo);
        const item = itemsBySerial.get(serialNo);

        this._imageCheckedSerials.add(serialNo);

        if (item && warranty?.ImageUrl) {
          item.ImageUrl = warranty.ImageUrl;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch product images for', rowsToFetch, err);
    }
  }

  _parseConfig() {
    try {
      const configData = this.el.getAttribute('data-service-results-table-config');

      if (!configData || configData === 'undefined' || configData.trim() === '') {
        return null;
      }

      return JSON.parse(configData);
    } catch (e) {
      console.error('Service flow table: configuration to parse is not valid JSON!', e);
      return null;
    }
  }

  /**
   * Determines whether a row should be treated as inactive.
   * A row is inactive when:
   *   - item.Returned is true, OR
   *   - the flow's service was already used (e.g. loss and damage), OR
   *   - this is a single-warranty table and the item has no valid date for that warranty
   * @param {object} item
   * @returns {boolean}
   */
  _isRowInactive(item) {
    if (item.Returned) return true;

    if (isLossAndDamageUsed(this._warrantyKey, item)) return true;
    if (this._warrantyKey && !checkDateValidity(item[this._warrantyKey])) {
      return true;
    }

    if (isEarmouldDisabledForFlow(this._config.FlowType, item, this._config)) {
      return true;
    }

    return isExpiredForFlow(this._config.FlowType, item);
  }

  _hasSerialNumbersOnSameOrder(item) {
    return item.HasSerialNumbersOnSameOrder === true;
  }

  /**
   * Creates a full <tr> element for one API result item.
   * Iterates over all columns (including checkbox and actions types).
   * @param {object} item
   * @returns {HTMLTableRowElement}
   */
  _createRow(item) {
    const tr = document.createElement('tr');
    const inactive = this._isRowInactive(item);
    tr.className = `service-results-table__row${
      inactive ? ' service-results-table__row--returned' : ''
    }`;
    tr.dataset.serialNo = String(item.SerialNo || '');

    if (item.RepairWarrantyEndDate) {
      tr.dataset.repairWarrantyDate = item.RepairWarrantyEndDate;
    }

    if (this._warrantyKey && item[this._warrantyKey]) {
      tr.dataset.selectedWarrantyDate = item[this._warrantyKey];
    }

    if (this._hasSerialNumbersOnSameOrder(item)) {
      tr.dataset.hasSerialNumbersOnSameOrder = 'true';
    }

    for (const col of this._columns) {
      if (col.type === 'checkbox') {
        tr.appendChild(this._createCheckboxCell(item));
      } else if (col.type === 'actions') {
        tr.appendChild(this._createActionsCell(item, col));
      } else {
        tr.appendChild(this._createCellByType(col, item));
      }
    }

    return tr;
  }

  /**
   * Creates a <td> with the standard label/value inner structure.
   * @param {string} labelText
   * @param {(valueEl: HTMLElement) => void} fillValue
   * @param {string[]} [extraClasses]
   * @returns {HTMLTableCellElement}
   */
  _createCell(labelText, fillValue, extraClasses = []) {
    const td = document.createElement('td');
    td.className = ['service-results-table__cell', ...extraClasses].filter(Boolean).join(' ');

    const labelSpan = document.createElement('span');
    labelSpan.className = 'service-results-table__cell-label';
    labelSpan.textContent = labelText;

    const valueEl = document.createElement('div');
    valueEl.className = 'service-results-table__cell-value';
    fillValue(valueEl);

    td.appendChild(labelSpan);
    td.appendChild(valueEl);
    return td;
  }

  /**
   * Creates a checkbox <input> element.
   * @param {string} serialNo
   * @param {string[]} [extraClasses]
   * @returns {HTMLInputElement}
   */
  _createCheckboxInput(serialNo, extraClasses = []) {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = ['service-results-table__checkbox', ...extraClasses]
      .filter(Boolean)
      .join(' ');
    input.value = serialNo;
    input.setAttribute('aria-label', `Select serial number ${serialNo}`);
    return input;
  }

  /**
   * Creates the desktop checkbox <td>.
   * @param {object} item
   * @returns {HTMLTableCellElement}
   */
  _createCheckboxCell(item) {
    const td = document.createElement('td');
    td.className =
      'service-results-table__cell service-results-table__cell--checkbox service-results-table__cell--checkbox-desktop';

    if (!this._isRowInactive(item)) {
      td.appendChild(this._createCheckboxInput(String(item.SerialNo || '')));
    }

    return td;
  }

  /**
   * Dispatches to the correct cell creator based on column type / key.
   * @param {object} column
   * @param {object} item
   * @returns {HTMLTableCellElement}
   */
  _createCellByType(column, item) {
    const label = String(column.label || '');
    const rawValue = item[column.key];

    if (column.type === 'warranty') {
      return this._createWarrantyCell(
        label,
        rawValue,
        item.Returned,
        isLossAndDamageUsed(column.key, item),
      );
    }

    if (column.key === 'SerialNo' && this._showCheckboxes) {
      return this._createSerialNoCellWithCheckbox(label, item);
    }

    return this._createCell(label, (valueEl) => {
      valueEl.textContent = rawValue ? String(rawValue) : '-';
    });
  }

  /**
   * Creates the SerialNo cell including the mobile checkbox when enabled.
   * @param {string} label
   * @param {object} item
   * @returns {HTMLTableCellElement}
   */
  _createSerialNoCellWithCheckbox(label, item) {
    const serialNo = String(item.SerialNo || '');

    return this._createCell(label, (valueEl) => {
      if (!this._isRowInactive(item)) {
        valueEl.appendChild(
          this._createCheckboxInput(serialNo, ['service-results-table__checkbox--mobile']),
        );
      }
      valueEl.appendChild(document.createTextNode(serialNo || '-'));
    });
  }

  /**
   * Creates a warranty date cell with a badge or dash.
   * @param {string} label
   * @param {string|null} dateValue
   * @param {boolean} isReturned
   * @param {boolean} [isUsed]
   * @returns {HTMLTableCellElement}
   */
  _createWarrantyCell(label, dateValue, isReturned, isUsed = false) {
    const formatString = this._config.DateFormat || 'dd-mm-yyyy';
    const { modifier, displayLabel, date, shortTextMode } = evaluateWarrantyDate(
      dateValue,
      isReturned,
      {
        expiredLabel: this._config.Translations?.expiredLabel,
        returnedLabel: this._config.Translations?.returnedLabel,
        usedLabel: this._config.Translations?.usedLabel,
        untilLabel: this._config.Translations?.until,
      },
      isUsed,
    );

    return this._createCell(label, (valueEl) => {
      if (!date) {
        const dash = document.createElement('span');
        dash.className = 'service-results-table__dash';
        dash.textContent = '-';
        valueEl.appendChild(dash);
        return;
      }
      const formattedDate = formatDate(date, formatString);

      const badge = document.createElement('span');
      badge.className = `service-results-table__badge${
        modifier ? ` service-results-table__badge--${modifier}` : ''
      }`;

      if (modifier === WARRANTY_MODIFIER.USED || modifier === WARRANTY_MODIFIER.RETURNED) {
        badge.textContent = displayLabel;
      } else if (displayLabel) {
        const fullText = `${displayLabel} ${formattedDate}`;
        const shortText = shortTextMode === 'date' ? formattedDate : displayLabel;
        badge.setAttribute('data-full-text', fullText);
        badge.setAttribute('data-short-text', shortText);
        badge.textContent = fullText;
      } else {
        badge.textContent = formattedDate;
      }

      valueEl.appendChild(badge);
    });
  }

  /**
   * Creates the actions <td>.
   * Renders a styled button (dropdown trigger) when col.dropdown is true,
   * or a text-link styled button (sidebar trigger) otherwise.
   * For returned items, actions are hidden only when col.dropdown is true.
   * @param {object} item
   * @param {object} col  The actions column definition from config
   * @returns {HTMLTableCellElement}
   */
  _createActionsCell(item, col) {
    const lblActions = String(col.label || this._config.Translations?.actionsLabel || 'Actions');
    const lblTrigger = String(col.triggerLabel || lblActions);

    return this._createCell(lblActions, (valueEl) => {
      if (this._isRowInactive(item) && col.dropdown) {
        return;
      }

      const dropdown = document.createElement('div');
      dropdown.className = 'ui-dropdown';
      dropdown.dataset.dropable = '';
      dropdown.dataset.portal = '';

      const btn = document.createElement('button');
      btn.type = 'button';

      if (col.dropdown) {
        btn.className = 'ui-dropdown__trigger';
        btn.setAttribute('aria-haspopup', 'true');
        btn.setAttribute('aria-expanded', 'false');
        btn.textContent = lblTrigger;

        const menu = document.createElement('ul');
        menu.className = 'ui-dropdown-menu';

        dropdown.appendChild(btn);
        dropdown.appendChild(menu);
        valueEl.appendChild(dropdown);
      } else {
        const hasSameOrderItems = this._hasSerialNumbersOnSameOrder(item);

        if (hasSameOrderItems) {
          btn.className = 'service-results-table__action-link';
          btn.setAttribute('data-action-sidebar', '');
          btn.textContent = lblTrigger;
          valueEl.appendChild(btn);
        }
      }
    });
  }

  _hasScroll() {
    const wrapper = this._refs.scrollWrapper;
    if (!wrapper) return false;

    return wrapper.scrollWidth > wrapper.clientWidth;
  }

  _renderEmptyState(searchWord) {
    const dataColCount = this._columns.length;
    const noResultsLabel = String(
      this._config.Translations?.noResultsLabel || 'Sorry! No results found for',
    );
    const noResultsSubLabel = String(
      this._config.Translations?.noResultsSubLabel || 'Please try and search for something else.',
    );

    const tr = document.createElement('tr');
    tr.className = 'service-results-table__row service-results-table__row--empty';

    const td = document.createElement('td');
    td.className = 'service-results-table__cell service-results-table__cell--empty';
    td.setAttribute('colspan', String(dataColCount));

    const wrapper = document.createElement('div');
    wrapper.className = 'service-results-table__empty-state';

    const icon = document.createElement('span');
    icon.className = 'service-results-table__empty-icon';
    icon.setAttribute('aria-hidden', 'true');

    const title = document.createElement('p');
    title.className = 'service-results-table__empty-title';
    title.textContent = noResultsLabel + ' "' + String(searchWord || '') + '"';

    const subtitle = document.createElement('p');
    subtitle.className = 'service-results-table__empty-subtitle';
    subtitle.textContent = noResultsSubLabel;

    wrapper.appendChild(icon);
    wrapper.appendChild(title);
    wrapper.appendChild(subtitle);
    td.appendChild(wrapper);
    tr.appendChild(td);

    return tr;
  }

  _adjustBadges() {
    requestAnimationFrame(() => {
      const badges = this._refs.tbody?.querySelectorAll(
        '.service-results-table__badge[data-full-text]',
      );
      if (badges) {
        const needsTooltip = this._hasScroll();

        badges.forEach((badge) => {
          if (needsTooltip) {
            badge.textContent = badge.getAttribute('data-short-text');
            badge.setAttribute('data-global-tooltip', badge.getAttribute('data-full-text'));
          } else {
            badge.textContent = badge.getAttribute('data-full-text');
            badge.removeAttribute('data-global-tooltip');
          }
        });
      }
    });
  }
}
